import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Spinner, Badge, ProgressBar } from 'react-bootstrap';
import authService from '../services/authService';
import html2pdf from 'html2pdf.js';
import { TextWithTooltips } from '../components/Tooltip';
import { colors } from '../brand-colors';

function UpdatedExitStrategy() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromCommandCenter = searchParams.get('from') === 'cc';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportData, setReportData] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [aiSections, setAiSections] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [isQuickStartUser, setIsQuickStartUser] = useState(false);
  const reportRef = useRef(null);

  useEffect(() => {
    initializePage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initializePage = async () => {
    setLoading(true);

    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check if quick-start user
    try {
      const statusResponse = await fetch('/api/quick-start/get-status', {
        method: 'GET',
        credentials: 'include'
      });
      if (statusResponse.ok) {
        const status = await statusResponse.json();
        setIsQuickStartUser(status.onboarding_path === 'quick_start' || status.onboarding_path === 'quick_start_exploring');
      }
    } catch (err) {
      console.error('Error checking quick-start status:', err);
    }

    await fetchMarketingReport();
    setLoading(false);
  };

  // Navigate to appropriate dashboard based on user type
  const handleBackToDashboard = () => {
    if (isQuickStartUser) {
      navigate('/quick-start-dashboard');
    } else {
      navigate('/dashboard/marketing-strategy');
    }
  };

  const fetchMarketingReport = async () => {
    try {
      // Add cache-busting to ensure fresh data after product edits
      const response = await fetch(`/api/marketing/get-exit-strategy-report?t=${Date.now()}`, {
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setReportData(data);

        // Debug: Log revenue model data
        if (data.revenue_model) {
          console.log('[EXIT STRATEGY] Revenue Model:', {
            recurring: data.revenue_model.recurring_percentage + '%',
            recurringCount: data.revenue_model.recurring_count,
            onetimeCount: data.revenue_model.onetime_count,
            totalProducts: data.revenue_model.total_products
          });
        }

        // Load saved AI sections if they exist
        if (data.ai_sections && Object.keys(data.ai_sections).length > 0) {
          console.log('[EXIT STRATEGY] Loaded saved AI sections:', Object.keys(data.ai_sections));
          setAiSections(data.ai_sections);
        } else {
          console.log('[EXIT STRATEGY] No saved AI sections found');
        }
      } else {
        const errorData = await response.json();
        console.error('Report error:', errorData);
        setError(errorData.error || 'Please complete Steps 1-4 before viewing your exit strategy report');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Error loading report: ' + err.message);
    }
  };

  const handleGenerateAIAnalysis = async () => {
    setGenerating(true);
    setError('');

    try {
      const response = await fetch('/api/marketing/generate-exit-analysis', {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAiSections(data);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to generate AI analysis');
      }
    } catch (err) {
      setError('Error generating analysis: ' + err.message);
    }

    setGenerating(false);
  };

  const getSeverityVariant = (severity) => {
    if (severity === 'critical') return 'danger';
    if (severity === 'high') return 'warning';
    if (severity === 'medium') return 'info';
    return 'secondary';
  };

  const getImpactColor = (impact) => {
    if (impact >= 8) return 'success';
    if (impact >= 5) return 'warning';
    return 'danger';
  };

  // Command Center feature keywords for annotation
  const doneForYouKeywords = [
    'crm', 'customer relationship management', 'contact management',
    'email automation', 'email sequence', 'email sequences', 'drip campaign', 'drip campaigns',
    'welcome sequence', 'welcome sequences', 'autoresponder', 'autoresponders',
    'sms marketing', 'sms campaign', 'sms campaigns', 'text campaign', 'text campaigns', 'text marketing',
    'marketing automation', 'marketing channels',
    'landing page', 'landing pages', 'sales page', 'sales pages', 'funnel', 'funnels',
    'sales funnel', 'sales funnels', 'opt-in page', 'opt-in pages', 'squeeze page', 'squeeze pages',
    'web form', 'web forms', 'lead capture form', 'lead capture forms',
    'appointment scheduling', 'booking system', 'calendar integration', 'scheduling system',
    'analytics', 'data analytics', 'kpi tracking', 'kpi', 'kpis', 'performance tracking',
    'reporting', 'dashboard', 'dashboards', 'roi tracking', 'conversion tracking',
    'social media scheduling', 'social posting', 'social media posting', 'content calendar',
    'workflow automation', 'automation', 'automations', 'automated workflow', 'automated workflows',
    'seo', 'search engine optimization',
    'content/organic growth', 'organic growth', 'content marketing',
    'facebook', 'instagram', 'linkedin', 'twitter', 'twitter/x', 'youtube', 'tiktok', 'pinterest',
    'social media channels', 'social media platforms', 'social channels'
  ];

  const easyToAddKeywords = [
    'lead management', 'lead tracking', 'pipeline', 'sales pipeline',
    'onboarding sequence', 'onboarding sequences', 'onboarding automation', 'onboarding system',
    'review collection', 'reputation management', 'reviews system', 'testimonial',
    'referral program', 'referral system', 'referral programs', 'affiliate program',
    'customer retention', 'retention rate', 'retention strategy'
  ];

  // Function to annotate text with Command Center markers (one per line, at the end)
  const annotateWithCommandCenter = (text) => {
    if (!text) return null;

    const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // Create regex patterns
    const doneForYouPattern = new RegExp(
      `(${doneForYouKeywords.map(escapeRegex).join('|')})`,
      'gi'
    );
    const easyToAddPattern = new RegExp(
      `(${easyToAddKeywords.map(escapeRegex).join('|')})`,
      'gi'
    );

    // Process each line
    const lines = text.split('\n');
    const annotatedLines = lines.map((line, index) => {
      const hasDoneForYou = doneForYouPattern.test(line);
      doneForYouPattern.lastIndex = 0; // Reset regex state
      const hasEasyToAdd = easyToAddPattern.test(line);
      easyToAddPattern.lastIndex = 0; // Reset regex state

      // Prioritize "done for you" over "easy to add"
      if (hasDoneForYou) {
        return (
          <span key={index}>
            {line}
            <span style={{
              color: '#198754',
              fontWeight: 'bold',
              marginLeft: '6px'
            }}> ✓</span>
            {'\n'}
          </span>
        );
      } else if (hasEasyToAdd) {
        return (
          <span key={index}>
            {line}
            <span style={{
              color: '#0dcaf0',
              fontWeight: 'bold',
              marginLeft: '6px'
            }}> ✧</span>
            {'\n'}
          </span>
        );
      }
      return <span key={index}>{line}{'\n'}</span>;
    });

    return annotatedLines;
  };

  const handleExportPDF = async () => {
    if (!reportRef.current) return;

    setExporting(true);

    try {
      const element = reportRef.current;
      const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

      const opt = {
        margin: [0.5, 0.5, 0.5, 0.5],
        filename: `Exit-Strategy-Report-${date.replace(/[,\s]+/g, '-')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true
        },
        jsPDF: {
          unit: 'in',
          format: 'letter',
          orientation: 'portrait'
        },
        pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
      };

      await html2pdf().set(opt).from(element).save();
    } catch (err) {
      console.error('PDF export error:', err);
      setError('Failed to export PDF. Please try again.');
    }

    setExporting(false);
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
        <p className="mt-3">Loading your exit strategy report...</p>
      </Container>
    );
  }

  if (error && !reportData) {
    return (
      <Container className="py-5">
        <Alert variant="warning">
          <Alert.Heading>
            {isQuickStartUser ? 'Exit Strategy Not Generated Yet' : 'Complete Your Marketing Strategy First'}
          </Alert.Heading>
          <p>
            {isQuickStartUser
              ? 'Please complete the Quick Start interview to generate your exit strategy.'
              : error}
          </p>
          <Button
            className="btn-brand-primary"
            onClick={() => navigate(isQuickStartUser ? '/quick-start' : '/dashboard/marketing-strategy')}
          >
            {isQuickStartUser ? 'Go to Quick Start' : 'Go to Marketing Strategy'}
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      {/* Export Button - Outside the ref so it doesn't appear in PDF */}
      <div className="d-flex justify-content-end mb-3">
        <Button
          variant="outline-primary"
          onClick={handleExportPDF}
          disabled={exporting}
        >
          {exporting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Exporting...
            </>
          ) : (
            'Export to PDF'
          )}
        </Button>
      </div>

      <div ref={reportRef}>
        <Row className="justify-content-center">
          <Col md={12} lg={10}>
            {error && <Alert variant="danger">{error}</Alert>}

            {/* Header */}
            <Card className="shadow mb-4">
              <Card.Body className="p-5">
                <h1 className="text-center mb-3">Marketing Strategy & Exit Valuation Report</h1>
                <p className="text-center text-muted mb-4">
                  Comprehensive analysis of how your marketing strategy impacts business valuation and exit readiness.
                </p>

              {reportData?.original_exit && (
                <Alert variant="info">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h5 className="mb-1" style={{ color: '#ffffff' }}>Your Exit Strategy</h5>
                      <h6 style={{ color: colors.goldLight, fontWeight: 500, marginBottom: 4 }}>
                        {reportData.original_exit.path === 'build_to_sell' && 'Build to Sell (3-7 Year Exit)'}
                        {reportData.original_exit.path === 'acquisition' && 'Scale for Acquisition (Venture Path)'}
                        {reportData.original_exit.path === 'lifestyle' && 'Lifestyle Business (Keep Forever)'}
                        {reportData.original_exit.path === 'legacy' && 'Legacy Business (Multi-Generational)'}
                        {!reportData.original_exit.path && 'Not specified'}
                      </h6>
                      {reportData.original_exit.target && (
                        <div style={{ color: colors.goldLight, fontWeight: 600, fontSize: '1.1rem' }}>
                          Goal: {reportData.original_exit.target}
                          {reportData.original_exit.timeline && (
                            <span style={{ fontWeight: 400, color: '#cbd5e0', fontSize: '0.95rem' }}>
                              {' '}by {reportData.original_exit.timeline}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <Button
                      size="sm"
                      style={{
                        backgroundColor: 'transparent',
                        border: `2px solid ${colors.goldLight}`,
                        color: colors.goldLight,
                      }}
                      onClick={() => navigate('/exit-strategy?edit=true')}
                    >
                      Edit Strategy
                    </Button>
                  </div>
                </Alert>
              )}
            </Card.Body>
          </Card>

          {/* Timeline Impact */}
          {reportData?.timeline_impact && (
            <Alert variant="warning" className="mb-4">
              <Alert.Heading>Timeline Impact Analysis</Alert.Heading>
              <p className="mb-0">{reportData.timeline_impact}</p>
            </Alert>
          )}

          {/* Marketing Maturity Score */}
          {reportData?.maturity_score && (
            <Card className="shadow mb-4">
              <Card.Body className="p-4">
                <h3 className="mb-3">Marketing Maturity Score</h3>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-2">
                    <span>Overall Marketing Readiness</span>
                    <strong>{reportData.maturity_score.overall}/10</strong>
                  </div>
                  <ProgressBar
                    now={reportData.maturity_score.overall * 10}
                    variant={getImpactColor(reportData.maturity_score.overall)}
                    style={{ height: '25px' }}
                  />
                </div>

                <Row className="mt-4">
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Acquisition Channels:</strong>
                      <ProgressBar
                        now={reportData.maturity_score.acquisition * 10}
                        label={`${reportData.maturity_score.acquisition}/10`}
                        variant={getImpactColor(reportData.maturity_score.acquisition)}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Retention Systems:</strong>
                      <ProgressBar
                        now={reportData.maturity_score.retention * 10}
                        label={`${reportData.maturity_score.retention}/10`}
                        variant={getImpactColor(reportData.maturity_score.retention)}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Viral/Referral:</strong>
                      <ProgressBar
                        now={reportData.maturity_score.viral * 10}
                        label={`${reportData.maturity_score.viral}/10`}
                        variant={getImpactColor(reportData.maturity_score.viral)}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="mb-3">
                      <strong>Data & Tracking:</strong>
                      <ProgressBar
                        now={reportData.maturity_score.data * 10}
                        label={`${reportData.maturity_score.data}/10`}
                        variant={getImpactColor(reportData.maturity_score.data)}
                      />
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Red Flags from Original Exit Analysis */}
          {reportData?.red_flags_triggered && reportData.red_flags_triggered.length > 0 && (
            <Card className="shadow mb-4 border-danger">
              <Card.Body className="p-4">
                <h3 className="mb-3">Red Flags Triggered (From Module 1 Analysis)</h3>
                <p className="text-muted mb-4">
                  These are red flags you were warned about in your original exit strategy analysis that are currently present in your business.
                </p>
                {reportData.red_flags_triggered.map((flag, index) => (
                  <Alert key={index} variant="danger" className="mb-3">
                    <strong>{flag.title}</strong>
                    <p className="mb-0 mt-2">{flag.description}</p>
                  </Alert>
                ))}
              </Card.Body>
            </Card>
          )}

          {/* Revenue Model Analysis */}
          {reportData?.revenue_model && (
            <Card className="shadow mb-4">
              <Card.Body className="p-4">
                <h3 className="mb-3">Revenue Model Analysis</h3>
                {reportData.revenue_model.has_hybrid && (
                  <Alert variant="info" className="mb-3" style={{ color: '#ffffff' }}>
                    <strong>Hybrid Model Detected:</strong> Your core product has both one-time and recurring components. Both are included in the analysis below.
                  </Alert>
                )}
                <Row>
                  <Col md={6}>
                    <Card className="text-center mb-3">
                      <Card.Body>
                        <h6 className="text-muted">Recurring Revenue</h6>
                        <h2 className={reportData.revenue_model.recurring_percentage >= 70 ? 'text-success' : reportData.revenue_model.recurring_percentage < 30 ? 'text-danger' : 'text-warning'}>
                          {reportData.revenue_model.recurring_percentage}%
                        </h2>
                        <small>{reportData.revenue_model.recurring_count} product(s){reportData.revenue_model.has_hybrid ? ' (incl. hybrid)' : ''}</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={6}>
                    <Card className="text-center mb-3">
                      <Card.Body>
                        <h6 className="text-muted">One-Time Revenue</h6>
                        <h2>
                          {reportData.revenue_model.total_products === 0
                            ? '0'
                            : (100 - reportData.revenue_model.recurring_percentage)
                          }%
                        </h2>
                        <small>{reportData.revenue_model.onetime_count} product(s){reportData.revenue_model.has_hybrid ? ' (incl. hybrid)' : ''}</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
                <Alert variant={reportData.revenue_model.recurring_percentage >= 70 ? 'success' : reportData.revenue_model.recurring_percentage < 30 ? 'danger' : 'warning'}>
                  <strong>Impact:</strong> {reportData.revenue_model.impact}
                  {reportData.revenue_model.recurring_percentage < 70 && (
                    <p className="mb-0 mt-2">
                      Tip: Businesses with 70%+ recurring revenue typically command 2-3x higher valuation multiples.
                    </p>
                  )}
                </Alert>
              </Card.Body>
            </Card>
          )}

          {/* Competitive Positioning */}
          {reportData?.competitive_positioning && reportData.competitive_positioning.length > 0 && (
            <Card className="shadow mb-4">
              <Card.Body className="p-4">
                <h3 className="mb-3">Competitive Positioning Summary</h3>
                <p className="text-muted mb-4">
                  How your competitors are positioned in the market (from Module 2 analysis).
                </p>
                {reportData.competitive_positioning.map((comp, index) => (
                  <Card key={index} className="mb-3 border-secondary">
                    <Card.Body>
                      <h5>{comp.name}</h5>
                      <p className="mb-2"><strong>Positioning:</strong> {comp.positioning}</p>
                      <p className="mb-0"><strong>Pricing:</strong> {comp.pricing}</p>
                    </Card.Body>
                  </Card>
                ))}
              </Card.Body>
            </Card>
          )}

          {/* Acquirer's Perspective - Only show for non-lifestyle exit paths */}
          {reportData?.acquirer_perspective && reportData.acquirer_perspective.buyer_type && reportData?.original_exit?.path !== 'lifestyle' && (
            <Card className="shadow mb-4 border-info">
              <Card.Body className="p-4">
                <h3 className="mb-3">What Acquirers Look For</h3>
                <Alert variant="info" style={{ color: '#ffffff' }}>
                  <strong>Typical Buyer Type:</strong> {reportData.acquirer_perspective.buyer_type}
                </Alert>

                <Row>
                  <Col md={6}>
                    <h5 className="mb-3">Key Metrics They Evaluate</h5>
                    <ul>
                      {reportData.acquirer_perspective.key_metrics?.map((metric, index) => (
                        <li key={index}><TextWithTooltips text={metric} /></li>
                      ))}
                    </ul>
                  </Col>
                  <Col md={6}>
                    <h5 className="mb-3">Dealbreakers</h5>
                    <ul className="text-danger">
                      {reportData.acquirer_perspective.dealbreakers?.map((dealbreaker, index) => {
                        // Annotate dealbreakers that Command Center addresses
                        const lower = dealbreaker.toLowerCase();
                        let annotation = null;

                        if (lower.includes('single channel')) {
                          annotation = 'Campaign HQ';
                        } else if (lower.includes('documented processes') || lower.includes('owner involvement') || lower.includes('complex operations')) {
                          annotation = 'AI SOP Assistant';
                        } else if (lower.includes('slowing growth') || lower.includes('declining revenue') || lower.includes('burn rate')) {
                          annotation = 'Profit Zone';
                        } else if (lower.includes('competitive position') || lower.includes('technical debt') || lower.includes('scalability') || lower.includes('customer service burden')) {
                          annotation = 'Profit Advisor';
                        } else if (lower.includes('churn')) {
                          annotation = 'Campaign HQ';
                        }

                        return (
                          <li key={index}>
                            <TextWithTooltips text={dealbreaker} />
                            {annotation && (
                              <span style={{ color: '#198754', fontWeight: 'bold', marginLeft: '6px' }}>
                                ✓ <TextWithTooltips text={annotation} />
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                    <p className="small text-muted mt-3 mb-0">
                      <span style={{ color: '#198754', fontWeight: 'bold' }}>✓</span> Available when you upgrade to the <TextWithTooltips text="Command Center" />
                    </p>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          )}

          {/* Critical Gaps */}
          {reportData?.gaps && reportData.gaps.length > 0 && (
            <Card className="shadow mb-4 border-warning">
              <Card.Body className="p-4">
                <h3 className="mb-3">Critical Gaps Affecting Valuation</h3>
                <p className="text-muted mb-3">
                  These deficiencies are reducing your business value and exit readiness. Addressing them should be your top priority.
                </p>

                {/* Command Center Upsell */}
                <Alert variant="light" className="mb-4 border" style={{ borderLeft: `4px solid ${colors.redDark}` }}>
                  <div className="d-flex align-items-start">
                    <div>
                      <strong style={{ color: colors.redDark, fontSize: '1.1rem' }}><TextWithTooltips text="Command Center" /> Help Available</strong>
                      <p className="mb-0 mt-1 small">
                        If you need help thinking through the implementation of any of the gaps or recommendations on this page,
                        the <strong><TextWithTooltips text="Profit Advisor" /></strong> will be there for you when you upgrade. It knows your business, your customers,
                        and your products. It will ask you questions and can help you make decisions—then guide you every step of the way.
                      </p>
                    </div>
                  </div>
                </Alert>

                {reportData.gaps.map((gap, index) => (
                  <Alert key={index} variant={getSeverityVariant(gap.severity)} className="mb-3">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <Alert.Heading as="h5">
                          {gap.title}
                          <Badge bg={getSeverityVariant(gap.severity)} className="ms-2">
                            {gap.severity.toUpperCase()}
                          </Badge>
                        </Alert.Heading>
                        <p className="mb-2">{gap.description}</p>
                        <p className="mb-0"><strong>Impact on Valuation:</strong> {gap.valuation_impact}</p>
                      </div>
                    </div>
                  </Alert>
                ))}
              </Card.Body>
            </Card>
          )}

          {/* Strengths */}
          {reportData?.strengths && reportData.strengths.length > 0 && (
            <Card className="shadow mb-4 border-success">
              <Card.Body className="p-4">
                <h3 className="mb-3">Marketing Strengths</h3>
                <p className="text-muted mb-4">
                  These are working well and adding value to your business.
                </p>

                {reportData.strengths.map((strength, index) => (
                  <Alert key={index} variant="success" className="mb-3">
                    <strong>{strength.title}</strong>
                    <p className="mb-0 mt-2">{strength.description}</p>
                  </Alert>
                ))}
              </Card.Body>
            </Card>
          )}

          {/* Priority Recommendations */}
          {reportData?.recommendations && reportData.recommendations.length > 0 && (
            <Card className="shadow mb-4">
              <Card.Body className="p-4">
                <h3 className="mb-3">Priority Recommendations</h3>
                <p className="text-muted mb-4">
                  Tackle these in order for maximum impact on business value.
                </p>

                {reportData.recommendations.map((rec, index) => (
                  <Card key={index} className="mb-3 border-primary">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <h5>
                          <Badge bg="primary" className="me-2">#{index + 1}</Badge>
                          {rec.title}
                        </h5>
                        <Badge bg="info">ROI: {rec.roi_potential}</Badge>
                      </div>
                      <p className="mb-2">{rec.description}</p>
                      <p className="mb-1"><strong>Why it matters:</strong> {rec.why}</p>
                      <p className="mb-0"><strong>Quick win:</strong> {rec.quick_win}</p>
                    </Card.Body>
                  </Card>
                ))}

                {/* Command Center Upsell */}
                <Alert variant="light" className="mt-4 border" style={{ borderLeft: `4px solid ${colors.redDark}` }}>
                  <div className="d-flex align-items-start">
                    <div>
                      <strong style={{ color: colors.redDark, fontSize: '1.1rem' }}>Need Help With Implementation?</strong>
                      <p className="mb-0 mt-1 small">
                        If you need help thinking through the implementation of any of the gaps or recommendations on this page,
                        the <strong><TextWithTooltips text="Profit Advisor" /></strong> will be there for you when you upgrade. It knows your business, your customers,
                        and your products. It will ask you questions and can help you make decisions—then guide you every step of the way.
                      </p>
                    </div>
                  </div>
                </Alert>
              </Card.Body>
            </Card>
          )}

          {/* Valuation Impact Summary */}
          {reportData?.valuation_impact && (
            <Card className="shadow mb-4 bg-light">
              <Card.Body className="p-4">
                <h3 className="mb-3">Valuation Impact Summary</h3>

                <Row>
                  <Col md={4}>
                    <Card className="text-center mb-3">
                      <Card.Body>
                        <h6 className="text-muted">Current Marketing Value</h6>
                        <h3 className="text-warning">{reportData.valuation_impact.current_multiplier}x</h3>
                        <small>revenue multiple</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="text-center mb-3">
                      <Card.Body>
                        <h6 className="text-muted">Potential with Improvements</h6>
                        <h3 className="text-success">{reportData.valuation_impact.potential_multiplier}x</h3>
                        <small>revenue multiple</small>
                      </Card.Body>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="text-center mb-3 border-success">
                      <Card.Body>
                        <h6 className="text-muted">Upside Potential</h6>
                        <h3 className="text-success">+{reportData.valuation_impact.upside_percentage}%</h3>
                        <small>valuation increase</small>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>

                <Alert variant="info" className="mb-0" style={{ color: '#ffffff' }}>
                  <strong>Bottom line:</strong> {reportData.valuation_impact.summary}
                </Alert>
              </Card.Body>
            </Card>
          )}

          {/* AI Strategic Roadmap */}
          <Card className="shadow mb-4">
            <Card.Body className="p-4">
              <h3 className="mb-3">AI-Powered Strategic Roadmap</h3>

              {/* Command Center Legend */}
              <Alert variant="light" className="mb-4 border" style={{ borderLeft: '4px solid #6f42c1' }}>
                <div className="d-flex flex-wrap gap-4">
                  <div className="d-flex align-items-center">
                    <Badge bg="success" className="me-2">✓</Badge>
                    <span className="small"><strong>Done for you</strong> in the <TextWithTooltips text="Command Center" /></span>
                  </div>
                  <div className="d-flex align-items-center">
                    <Badge bg="info" className="me-2">✧</Badge>
                    <span className="small"><strong>Easy to add</strong> with <TextWithTooltips text="Profit Advisor" /></span>
                  </div>
                </div>
              </Alert>

              {!aiSections ? (
                <div className="text-center py-4">
                  <p className="text-muted mb-3">
                    Generate a comprehensive AI-powered roadmap with tactical recommendations, milestones,
                    growth strategy, and infrastructure priorities based on your current data.
                  </p>
                  <Button
                    size="lg"
                    className="btn-brand-primary"
                    onClick={handleGenerateAIAnalysis}
                    disabled={generating}
                  >
                    {generating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Generating Roadmap... (this may take 30-60 seconds)
                      </>
                    ) : (
                      'Generate Strategic Roadmap'
                    )}
                  </Button>
                </div>
              ) : (
                <>
                  {/* Tactical 90-Day Recommendations */}
                  {aiSections.tactical_90_day && (
                    <Card className="mb-3 border-primary">
                      <Card.Body>
                        <h5 className="mb-3">Tactical 90-Day Recommendations</h5>
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                          {annotateWithCommandCenter(aiSections.tactical_90_day)}
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Milestones & Timeline */}
                  {aiSections.milestones_timeline && (
                    <Card className="mb-3 border-success">
                      <Card.Body>
                        <h5 className="mb-3">Milestones & Timeline (12/24/36 Months)</h5>
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                          {annotateWithCommandCenter(aiSections.milestones_timeline)}
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Growth Strategy */}
                  {aiSections.growth_strategy && (
                    <Card className="mb-3 border-warning">
                      <Card.Body>
                        <h5 className="mb-3">Growth Strategy & Budget Allocation</h5>
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                          {annotateWithCommandCenter(aiSections.growth_strategy)}
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  {/* Infrastructure Roadmap */}
                  {aiSections.infrastructure_roadmap && (
                    <Card className="mb-3 border-info">
                      <Card.Body>
                        <h5 className="mb-3">Infrastructure Roadmap</h5>
                        <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                          {annotateWithCommandCenter(aiSections.infrastructure_roadmap)}
                        </div>
                      </Card.Body>
                    </Card>
                  )}

                  <Button
                    variant="outline-primary"
                    onClick={handleGenerateAIAnalysis}
                    disabled={generating}
                    className="mt-3"
                  >
                    {generating ? 'Regenerating...' : 'Regenerate Roadmap'}
                  </Button>
                </>
              )}
            </Card.Body>
          </Card>

          {/* Next Steps - hide when viewing from Command Center */}
          {!fromCommandCenter && (
            <Card className="shadow mb-4 border-primary">
              <Card.Body className="p-4">
                <h3 className="mb-3">Next Steps</h3>
                <Alert variant="success">
                  <strong>Command Center</strong>
                  <p className="mb-0 mt-2">
                    In the Command Center, you'll work with your AI coach to design a bespoke
                    implementation plan for your business. Your AI agents will then help you execute the plan—building
                    referral programs, retention automation, viral mechanics, and more—all with your oversight and guidance.
                  </p>
                </Alert>
              </Card.Body>
            </Card>
          )}

          {/* Profit Map CTA - hide when viewing from Command Center */}
          {!fromCommandCenter && (
            <Card className="mt-5 shadow-lg border-0" style={{ background: `linear-gradient(135deg, #667eea 0%, ${colors.navyDark} 100%)` }}>
              <Card.Body className="text-white text-center py-5">
                <h2 className="display-5 fw-bold mb-3">Great. You've got your number. Now what?</h2>
                <p className="lead mb-4">
                  Without a plan, you're drifting — a dinghy in the middle of the ocean with no paddle.
                  The Profit Map looks at your business twice: once as a marketer, once as an investor.
                  Then it tells you exactly what needs to happen.
                </p>
                <Button
                  size="lg"
                  variant="light"
                  className="px-5 py-3 fw-bold"
                  style={{ color: '#ffffff', backgroundColor: 'transparent', borderColor: '#ffffff' }}
                  onClick={() => navigate('/battle-plan')}
                >
                  Let's Map How to Get You There
                </Button>
              </Card.Body>
            </Card>
          )}

        </Col>
      </Row>
      </div>

      {/* Action Buttons - Outside ref so they don't appear in PDF */}
      <Row className="justify-content-center">
        <Col md={12} lg={10}>
          <div className="d-flex justify-content-between mt-4">
            <Button
              variant="outline-secondary"
              onClick={() => fromCommandCenter ? navigate('/command-center/exit-strategy') : handleBackToDashboard()}
            >
              {fromCommandCenter ? '← Back to Exit Strategy' : '← Back to Dashboard'}
            </Button>
            {!fromCommandCenter && (
              <Button
                size="lg"
                className="btn-brand-primary"
                style={{ color: '#ffffff' }}
                onClick={() => navigate('/battle-plan')}
              >
                Let's Map How to Get You There
              </Button>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default UpdatedExitStrategy;
